<template>
    <div class="toast" :class="[`toast--${toast.type}`, { 'toast--dragging': dragging }]" role="status"
        aria-live="polite" :style="itemStyle" :data-swiping="swipingOut"
        @mouseenter="OnMouseEnter" @mouseleave="OnMouseLeave"
        @pointerdown="OnPointerDown" @pointermove="OnPointerMove"
        @pointerup="OnPointerUp" @pointercancel="OnPointerUp"
        @click="OnBodyClick">
        <!-- Icon centers against the text block only (align-items: center on the
             head row). The error-id row lives outside this row, so its height
             never drags the icon's center downward. -->
        <div class="toast__head">
            <span class="toast__icon" aria-hidden="true">
                <i :class="iconClass"></i>
            </span>

            <div class="toast__body">
                <p v-if="toast.title" class="toast__title">{{ toast.title }}</p>
                <p class="toast__message">{{ toast.message }}</p>
            </div>

            <button type="button" class="toast__close" aria-label="Cerrar notificación" @click="Dismiss">
                <i class="bi bi-x-lg"></i>
            </button>
        </div>

        <div v-if="toast.errorId" class="toast__ref">
            <span class="toast__ref-label">Error ID:</span>
            <code class="toast__ref-id">{{ toast.errorId }}</code>
            <button type="button" class="toast__ref-copy" :aria-label="copyLabel" @click="CopyErrorId">
                <i :class="copied ? 'bi bi-check-lg' : 'bi bi-clipboard'"></i>
                <span>{{ copied ? 'Copiado' : 'Copiar ID' }}</span>
            </button>
        </div>

        <span v-if="toast.duration > 0" class="toast__progress" :class="{ 'toast__progress--paused': paused }"
            aria-hidden="true"></span>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

const ICONS = {
    success: 'bi bi-check-circle-fill',
    error: 'bi bi-x-circle-fill',
    warning: 'bi bi-exclamation-triangle-fill',
    info: 'bi bi-info-circle-fill'
};

// Swipe thresholds. Dismiss on a decisive drag OR a quick flick — a flick still
// needs a little travel so a stationary tap can never register as one.
const SWIPE_DISTANCE = 96;      // px of travel that dismisses on its own
const FLICK_VELOCITY = 0.35;    // px/ms that dismisses when paired with...
const FLICK_MIN_DISTANCE = 24;  // ...at least this much travel

export default defineComponent({
    name: 'ToastItem',

    props: {
        toast: {
            type: Object,
            required: true
        }
    },

    emits: ['dismiss', 'measure'],

    data() {
        return {
            // Auto-dismiss bookkeeping. `remaining` shrinks as the toast is
            // hovered/un-hovered so the pause matches what the progress bar shows.
            timerId: null,
            remaining: this.toast.duration,
            startedAt: 0,
            paused: false,

            // Swipe-to-dismiss state.
            dragging: false,
            offset: 0,
            pointerId: null,
            startX: 0,
            dragStartAt: 0,
            snapping: false,
            swipingOut: false,
            swipeOutX: 0,
            // True once a pointer press has traveled far enough to be a drag, so
            // the trailing click it emits doesn't register as a tap-to-dismiss.
            didDrag: false,
            hovering: false,
            dismissTimeoutId: null,
            snapTimeoutId: null,

            // Reports own height to the layer so it can position the stack.
            resizeObserver: null,

            // Error-id copy feedback: flips to a check + "Copiado" briefly.
            copied: false,
            copiedTimeoutId: null
        };
    },

    computed: {
        iconClass() {
            return ICONS[this.toast.type] ?? ICONS.info;
        },

        // Single dynamic style hook (allowed inline exception — all values are
        // runtime-driven). `--toast-duration` feeds the progress bar; transform/
        // opacity/transition are only emitted while the toast is being dragged,
        // snapping back, or flung out, so they never fight the container's
        // enter/leave animations the rest of the time.
        itemStyle() {
            const style = { '--toast-duration': `${this.toast.duration}ms` };

            if (this.dragging) {
                style.transform = `translateX(${this.offset}px)`;
                style.opacity = this.dragOpacity;
                style.transition = 'none';
            } else if (this.swipingOut) {
                style.transform = `translateX(${this.swipeOutX}px)`;
                style.opacity = 0;
                style.transition = 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1), opacity 200ms cubic-bezier(0.23, 1, 0.32, 1)';
            } else if (this.snapping) {
                style.transform = 'translateX(0)';
                style.opacity = 1;
                style.transition = 'transform 320ms cubic-bezier(0.23, 1, 0.32, 1), opacity 200ms ease';
            }

            return style;
        },

        dragOpacity() {
            // Fade as it travels, but never fully vanish before release.
            return 1 - Math.min(Math.abs(this.offset) / 240, 0.55);
        },

        copyLabel() {
            return this.copied ? 'ID de error copiado' : 'Copiar ID de error';
        }
    },

    mounted() {
        this.StartTimer();

        // Report height now (before the first paint the layer positions from)
        // and on any later reflow — e.g. text rewrapping when the viewport width
        // changes — so stack offsets stay exact.
        this.ReportHeight();
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.ReportHeight);
            this.resizeObserver.observe(this.$el);
        }
    },

    unmounted() {
        this.ClearTimer();
        clearTimeout(this.dismissTimeoutId);
        clearTimeout(this.snapTimeoutId);
        clearTimeout(this.copiedTimeoutId);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    },

    methods: {
        ReportHeight() {
            this.$emit('measure', this.toast.id, this.$el.offsetHeight);
        },

        StartTimer() {
            // duration 0 == persistent: no auto-dismiss.
            if (this.toast.duration <= 0) {
                return;
            }

            this.startedAt = Date.now();
            this.timerId = setTimeout(this.Dismiss, this.remaining);
        },

        ClearTimer() {
            if (this.timerId !== null) {
                clearTimeout(this.timerId);
                this.timerId = null;
            }
        },

        PauseTimer() {
            if (this.toast.duration <= 0 || this.paused) {
                return;
            }

            this.paused = true;
            this.remaining -= Date.now() - this.startedAt;
            this.ClearTimer();
        },

        ResumeTimer() {
            if (this.toast.duration <= 0 || !this.paused) {
                return;
            }

            this.paused = false;
            this.StartTimer();
        },

        OnMouseEnter() {
            this.hovering = true;
            this.PauseTimer();
        },

        OnMouseLeave() {
            this.hovering = false;
            this.ResumeTimer();
        },

        OnPointerDown(event) {
            // Let the close/copy buttons handle their own clicks; primary button /
            // touch only.
            if (event.button === 2 || event.target.closest('.toast__close') || event.target.closest('.toast__ref-copy')) {
                return;
            }

            this.dragging = true;
            this.snapping = false;
            this.didDrag = false;
            this.offset = 0;
            this.startX = event.clientX;
            this.pointerId = event.pointerId;
            this.dragStartAt = Date.now();

            // Keep receiving moves even if the pointer leaves the toast bounds.
            // Guarded: capture can throw if the pointer is already gone — that
            // must never abort the drag.
            try {
                event.currentTarget.setPointerCapture(event.pointerId);
            } catch (e) {
                // no-op — drag still works without capture
            }

            this.PauseTimer();
        },

        OnPointerMove(event) {
            if (!this.dragging || event.pointerId !== this.pointerId) {
                return;
            }

            this.offset = event.clientX - this.startX;
            if (Math.abs(this.offset) > 4) {
                this.didDrag = true;
            }
        },

        OnPointerUp(event) {
            if (!this.dragging || event.pointerId !== this.pointerId) {
                return;
            }

            this.dragging = false;
            this.pointerId = null;

            const distance = Math.abs(this.offset);
            const velocity = distance / Math.max(Date.now() - this.dragStartAt, 1);
            const shouldDismiss = distance > SWIPE_DISTANCE
                || (velocity > FLICK_VELOCITY && distance > FLICK_MIN_DISTANCE);

            if (shouldDismiss) {
                // Fling out along the drag direction, then remove. The leave hook
                // reads data-swiping and collapses height only, so the toast
                // doesn't re-appear to slide a second time.
                this.swipeOutX = (this.offset < 0 ? -1 : 1) * (distance + 480);
                this.swipingOut = true;
                this.dismissTimeoutId = setTimeout(this.Dismiss, 200);
                return;
            }

            // Spring back to rest and resume the countdown, unless the pointer
            // is still hovering — mouseleave will resume it when it eventually
            // does.
            this.snapping = true;
            this.offset = 0;
            this.snapTimeoutId = setTimeout(() => { this.snapping = false; }, 320);
            if (!this.hovering) {
                this.ResumeTimer();
            }
        },

        async CopyErrorId() {
            const id = this.toast.errorId;
            if (!id) {
                return;
            }

            try {
                await navigator.clipboard.writeText(id);
            } catch (e) {
                // Clipboard API can be unavailable (insecure context / old
                // webview). Fall back to a hidden textarea + execCommand.
                this.FallbackCopy(id);
            }

            this.copied = true;
            clearTimeout(this.copiedTimeoutId);
            this.copiedTimeoutId = setTimeout(() => { this.copied = false; }, 2000);
        },

        FallbackCopy(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
            } catch (e) {
                // Nothing else to try — the id is still visible for manual copy.
            }
            document.body.removeChild(textarea);
        },

        OnBodyClick(event) {
            // Tap anywhere on the body dismisses — but not on the copy button
            // (it copies) or the close button (it dismisses itself), and not on
            // the click a swipe leaves behind.
            if (event.target.closest('.toast__ref-copy') || event.target.closest('.toast__close')) {
                return;
            }
            if (this.didDrag) {
                return;
            }
            this.Dismiss();
        },

        Dismiss() {
            this.ClearTimer();
            this.$emit('dismiss', this.toast.id);
        }
    }
});
</script>

<style scoped>
.toast {
    /* Brand-true light surface: the card carries a soft per-type tint, the icon
       tile is a white shade lifted off it, and the hairline border runs a more
       opaque draw of the same accent so the toast reads as one tinted object. */
    --toast-surface: var(--toast-tint, #ffffff);
    --toast-icon-bg: rgba(255, 255, 255, 0.82);
    --toast-border-accent: var(--toast-tint-strong, var(--toast-line));
    --toast-ink: #1f1f2c;          /* Raisin Black — brand */
    --toast-ink-dim: #5c5872;      /* raisin-violet, tinted from the ink (never flat grey) */
    --toast-line: rgba(31, 31, 44, 0.08);
    --ease-out: cubic-bezier(0.23, 1, 0.32, 1);

    position: relative;
    display: flex;
    flex-direction: column;
    /* border-box: the reported offsetHeight (used by the layer for stack
       offsets) then equals the padding-box width intent, keeping the geometry
       predictable. */
    box-sizing: border-box;
    width: 360px;
    max-width: calc(100vw - 32px);
    padding: 15px 15px 15px 15px;
    overflow: hidden;
    border-radius: 14px;
    background-color: var(--toast-surface);
    border: 1px solid var(--toast-border-accent);
    box-shadow: 0 14px 34px -16px rgba(31, 31, 44, 0.32), 0 2px 6px -3px rgba(31, 31, 44, 0.12);
    pointer-events: auto;
    cursor: grab;
    touch-action: pan-y;
    user-select: none;
    transition: box-shadow 200ms var(--ease-out);
}

.toast--dragging {
    cursor: grabbing;
}

@media (max-width: 600px) {
    /* Fill the full-width slot on mobile. */
    .toast {
        width: 100%;
        max-width: none;
    }
}

/* Per-type accent — carried by the icon tile and the progress bar. Pitched deep
   enough to clear 4.5:1 against white; explicit rgba tints (not color-mix) so
   they hold up on older iPad Safari webviews. Info reuses the brand --Info hue. */
/* --toast-tint is a SOLID light shade (accent blended onto white ~10-12%), not a
   translucent overlay, so the card never lets the page bleed through. The border
   keeps the stronger, more saturated accent draw. */
.toast--success { --toast-accent: #12a150; --toast-tint: #e7f6ed; --toast-tint-strong: rgba(18, 161, 80, 0.26); }
.toast--error   { --toast-accent: #e5484d; --toast-tint: #fceded; --toast-tint-strong: rgba(229, 72, 77, 0.26); }
.toast--warning { --toast-accent: #bd7b0c; --toast-tint: #fef4e5; --toast-tint-strong: rgba(189, 123, 12, 0.28); }
.toast--info    { --toast-accent: #3a5cd4; --toast-tint: #ebeffb; --toast-tint-strong: rgba(58, 92, 212, 0.26); }

@media (hover: hover) and (pointer: fine) {
    .toast:hover {
        box-shadow: 0 20px 44px -16px rgba(31, 31, 44, 0.38), 0 3px 8px -3px rgba(31, 31, 44, 0.14);
    }
}

/* Icon | text | close, vertically centered as a unit. Because the error-id row
   sits below this head (not inside it), the icon centers on the message/title
   block alone. */
.toast__head {
    display: flex;
    align-items: center;
    gap: 12px;
}

.toast__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background-color: var(--toast-icon-bg);
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 2px rgba(31, 31, 44, 0.06);
    color: var(--toast-accent);
    font-size: 16px;
}

/* Target the <i> directly: App.vue ships global .bi-check-circle-fill{color:green}
   / .bi-x-circle-fill{color:red} rules that would otherwise override the
   inherited accent on the success/error icons. This scoped selector outranks
   those single-class globals so all four icons use the toast's own accent. */
.toast__icon i {
    color: var(--toast-accent);
}

.toast__body {
    flex: 1;
    min-width: 0;
}

.toast__title {
    margin: 0 0 3px;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    line-height: 1.3;
    letter-spacing: -0.005em;
    color: var(--toast-ink);
}

.toast__message {
    margin: 0;
    font-family: 'Synonym', sans-serif;
    font-size: 13.5px;
    line-height: 1.45;
    color: var(--toast-ink-dim);
    word-break: break-word;
}

/* Error correlation-id row: shown only on uncontrolled-error toasts. The id is
   monospaced and truncates; the copy button sits beside it. */
.toast__ref {
    display: flex;
    align-items: center;
    gap: 8px;
    /* Indent past the icon column (icon 30px + head gap 12px) so the row lines
       up under the message text, not under the icon. */
    margin-left: 42px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--toast-line);
}

.toast__ref-label {
    flex-shrink: 0;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    color: var(--toast-ink-dim);
}

.toast__ref-id {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
    font-size: 11px;
    color: var(--toast-ink);
}

.toast__ref-copy {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border: 1px solid var(--toast-line);
    border-radius: 7px;
    background-color: transparent;
    color: var(--toast-ink-dim);
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    cursor: pointer;
    transition: background-color 150ms var(--ease-out), color 150ms var(--ease-out), border-color 150ms var(--ease-out);
}

.toast__ref-copy:hover {
    background-color: rgba(31, 31, 44, 0.06);
    color: var(--toast-ink);
    border-color: rgba(31, 31, 44, 0.16);
}

.toast__ref-copy:active {
    transform: scale(0.96);
}

.toast__close {
    flex-shrink: 0;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: -3px -3px 0 0;
    padding: 0;
    border: 0;
    border-radius: 7px;
    background-color: transparent;
    color: var(--toast-ink-dim);
    font-size: 12px;
    cursor: pointer;
    transition: background-color 150ms var(--ease-out), color 150ms var(--ease-out), transform 150ms var(--ease-out);
}

.toast__close:hover {
    background-color: rgba(31, 31, 44, 0.06);
    color: var(--toast-ink);
}

.toast__close:active {
    transform: scale(0.9);
}

/* Countdown bar depletes left→right over the toast's lifetime. Pausing the
   sweep on hover/drag matches the paused JS timer. */
.toast__progress {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 3px;
    width: 100%;
    transform-origin: left center;
    background-color: var(--toast-accent);
    opacity: 0.85;
    animation: toast-progress var(--toast-duration) linear forwards;
}

.toast__progress--paused {
    animation-play-state: paused;
}

@keyframes toast-progress {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
}

@media (prefers-reduced-motion: reduce) {
    /* Keep the bar as a static remaining-time hint, drop the sweep. */
    .toast__progress {
        animation: none;
    }
}
</style>
